import React, { createContext, useContext, useLayoutEffect, useRef } from 'react';

export const PublicPageDraftContext = createContext(null);

export const COLOR_PRESETS = [
  { id: 'default', label: 'Default', background: '', text: '' },
  { id: 'white', label: 'White', background: '#ffffff', text: '#0a0a0a' },
  { id: 'canvas', label: 'Canvas', background: '#fbfbf5', text: '#0a0a0a' },
  { id: 'charcoal', label: 'Charcoal', background: '#3a3a3a', text: '#ffffff' },
  { id: 'night', label: 'Night', background: '#000000', text: '#ffffff' },
];

const SectionLayoutContext = createContext({ order: [], colors: {}, hidden: [] });

export function isSectionHidden(saved, id) {
  return Array.isArray(saved?.hidden_sections) && saved.hidden_sections.includes(id);
}

export function SectionLayoutProvider({ order = [], colors = {}, hidden = [], children }) {
  return (
    <SectionLayoutContext.Provider value={{ order, colors, hidden }}>
      {children}
    </SectionLayoutContext.Provider>
  );
}

export function sectionColors(saved, id) {
  const row = saved?.section_colors?.[id];
  if (!row || (!row.background && !row.text)) return null;
  return row;
}

function paintElement(el, tone) {
  if (!el) return;
  el.classList.remove('cms-section-tone', 'cms-section-tone-text');
  el.style.removeProperty('--cms-section-bg');
  el.style.removeProperty('--cms-section-fg');
  if (!tone?.background && !tone?.text) return;
  el.classList.add('cms-section-tone');
  if (tone.background) el.style.setProperty('--cms-section-bg', tone.background);
  if (tone.text) {
    el.classList.add('cms-section-tone-text');
    el.style.setProperty('--cms-section-fg', tone.text);
  }
}

export function Painted({ id, tone, children }) {
  const ref = useRef(null);
  const draft = useContext(PublicPageDraftContext);
  const inPreview = Boolean(draft);

  useLayoutEffect(() => {
    const el = ref.current?.firstElementChild;
    if (!el) return undefined;
    if (id) el.setAttribute('data-cms-section', id);
    el.classList.toggle('cms-preview-target', inPreview);
    paintElement(el, tone);
    return () => {
      el.removeAttribute('data-cms-section');
      el.classList.remove('cms-preview-target');
      paintElement(el, null);
    };
  }, [tone, id, inPreview]);

  return (
    <div ref={ref} className="contents">
      {children}
    </div>
  );
}

export function renderSections(order, blocks, saved) {
  return order
    .filter((id) => !isSectionHidden(saved, id))
    .map((id) => (
      <Painted key={id} id={id} tone={sectionColors(saved, id)}>
        {blocks[id] || null}
      </Painted>
    ));
}

/** Keeps a fixed JSX tree, but lets admin reorder and recolor the block. */
export function SectionSlot({ id, children }) {
  const { order, colors, hidden } = useContext(SectionLayoutContext);
  const index = Array.isArray(order) ? order.indexOf(id) : -1;
  const tone = colors?.[id] || null;
  if (Array.isArray(hidden) && hidden.includes(id)) return null;

  return (
    <div className="flex flex-col" style={index >= 0 ? { order: index } : undefined}>
      <Painted id={id} tone={tone?.background || tone?.text ? tone : null}>{children}</Painted>
    </div>
  );
}
